/**
 * 🔥 PHASE 4: Heartbeat Metrics Tracking
 * 
 * Tracks comprehensive heartbeat health metrics for WebSocket connections.
 * Monitors Socket.IO built-in heartbeat performance and connection staleness.
 */

export class HeartbeatMetrics {
  constructor(serverKey, options = {}) {
    this.serverKey = serverKey;
    this.windowSize = options.windowSize || 100; // Track last 100 measurements
    
    // Ping/Pong metrics
    this.totalPings = 0;
    this.totalPongs = 0;
    this.latencies = [];
    this.missedPings = 0;
    
    // Connection staleness metrics
    this.stalenessChecks = 0;
    this.stalenessDetections = 0;
    this.lastActivityTime = Date.now();
    
    // Reconnection metrics
    this.reconnections = 0;
    this.reconnectionSuccesses = 0;
    this.reconnectionFailures = 0;
    
    // Time tracking
    this.startTime = Date.now();
    this.lastPingTime = null;
    this.lastPongTime = null;
  }

  /**
   * Record ping sent
   */
  recordPingSent() {
    this.totalPings++;
    this.lastPingTime = Date.now();
    this.lastActivityTime = Date.now();
  }

  /**
   * Record pong received
   * @param {number} latency - Latency in milliseconds (optional)
   */
  recordPongReceived(latency) {
    this.totalPongs++;
    this.lastPongTime = Date.now();
    this.lastActivityTime = Date.now();
    
    // Record latency if provided
    if (latency !== undefined && latency !== null && !isNaN(latency)) {
      this.latencies.push(latency);
      
      // Keep only last N measurements
      if (this.latencies.length > this.windowSize) {
        this.latencies.shift();
      }
    }
    // Calculate latency from lastPingTime if available
    else if (this.lastPingTime) {
      const calculatedLatency = Date.now() - this.lastPingTime;
      this.latencies.push(calculatedLatency);
      
      if (this.latencies.length > this.windowSize) {
        this.latencies.shift();
      }
    }
  }

  /**
   * Record missed ping
   */
  recordMissedPing() {
    this.missedPings++;
  }

  /**
   * Record staleness check
   * @param {boolean} isStale - Whether connection is stale
   */
  recordStalenessCheck(isStale) {
    this.stalenessChecks++;
    if (isStale) {
      this.stalenessDetections++;
    }
  }

  /**
   * Record activity (any message received)
   */
  recordActivity() {
    this.lastActivityTime = Date.now();
  }

  /**
   * Record reconnection attempt
   * @param {boolean} success - Whether reconnection succeeded
   */
  recordReconnection(success) {
    this.reconnections++;
    if (success) {
      this.reconnectionSuccesses++;
    } else {
      this.reconnectionFailures++;
    }
  }

  /**
   * Get heartbeat success rate (%)
   */
  getSuccessRate() {
    if (this.totalPings === 0) return 100;
    return ((this.totalPongs / this.totalPings) * 100).toFixed(2);
  }

  /**
   * Get average latency (ms)
   */
  getAverageLatency() {
    if (this.latencies.length === 0) return 0;
    const sum = this.latencies.reduce((a, b) => a + b, 0);
    return Math.round(sum / this.latencies.length);
  }

  /**
   * Get minimum latency (ms)
   */
  getMinLatency() {
    if (this.latencies.length === 0) return 0;
    return Math.min(...this.latencies);
  }

  /**
   * Get maximum latency (ms)
   */
  getMaxLatency() {
    if (this.latencies.length === 0) return 0;
    return Math.max(...this.latencies);
  }

  /**
   * Get time since last activity (ms)
   */
  getTimeSinceLastActivity() {
    return Date.now() - this.lastActivityTime;
  }

  /**
   * Get staleness detection rate (%)
   */
  getStalenessRate() {
    if (this.stalenessChecks === 0) return 0;
    return ((this.stalenessDetections / this.stalenessChecks) * 100).toFixed(2);
  }

  /**
   * Get reconnection success rate (%)
   */
  getReconnectionSuccessRate() {
    if (this.reconnections === 0) return 100;
    return ((this.reconnectionSuccesses / this.reconnections) * 100).toFixed(2);
  }

  /**
   * Get uptime (ms)
   */
  getUptime() {
    return Date.now() - this.startTime;
  }

  /**
   * Get comprehensive metrics
   */
  getMetrics() {
    return {
      serverKey: this.serverKey,
      
      // Ping/Pong metrics
      heartbeat: {
        totalPings: this.totalPings,
        totalPongs: this.totalPongs,
        missedPings: this.missedPings,
        successRate: `${this.getSuccessRate()}%`,
        latency: {
          average: `${this.getAverageLatency()}ms`,
          min: `${this.getMinLatency()}ms`,
          max: `${this.getMaxLatency()}ms`,
          samples: this.latencies.length
        }
      },
      
      // Connection staleness
      staleness: {
        checks: this.stalenessChecks,
        detections: this.stalenessDetections,
        rate: `${this.getStalenessRate()}%`,
        timeSinceLastActivity: `${Math.round(this.getTimeSinceLastActivity() / 1000)}s`
      },
      
      // Reconnection metrics
      reconnection: {
        total: this.reconnections,
        successes: this.reconnectionSuccesses,
        failures: this.reconnectionFailures,
        successRate: `${this.getReconnectionSuccessRate()}%`
      },
      
      // Time tracking
      timing: {
        uptime: `${Math.round(this.getUptime() / 1000)}s`,
        lastPing: this.lastPingTime ? new Date(this.lastPingTime).toISOString() : null,
        lastPong: this.lastPongTime ? new Date(this.lastPongTime).toISOString() : null,
        lastActivity: new Date(this.lastActivityTime).toISOString()
      }
    };
  }

  /**
   * Get summary for logging
   */
  getSummary() {
    return {
      serverKey: this.serverKey,
      successRate: this.getSuccessRate(),
      avgLatency: this.getAverageLatency(),
      missedPings: this.missedPings,
      reconnections: this.reconnections,
      uptime: Math.round(this.getUptime() / 1000)
    };
  }

  /**
   * Reset all metrics
   */
  reset() {
    this.totalPings = 0;
    this.totalPongs = 0;
    this.latencies = [];
    this.missedPings = 0;
    this.stalenessChecks = 0;
    this.stalenessDetections = 0;
    this.reconnections = 0;
    this.reconnectionSuccesses = 0;
    this.reconnectionFailures = 0;
    this.startTime = Date.now();
    this.lastActivityTime = Date.now();
    this.lastPingTime = null;
    this.lastPongTime = null;
  }

  /**
   * Export metrics to JSON
   */
  toJSON() {
    return this.getMetrics();
  }
}

export default HeartbeatMetrics;

